import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import * as React from 'react';
import { combineEpics, Epic } from 'redux-observable';
import { Observable } from 'rxjs';

import {
    FETCH_PAYMENT_DATA,
    MAKE_RESET_WALLET_LIMIT_REQUEST,
    MAKE_UPDATE_WALLET_LIMIT_REQUEST,
    RESET_WALLET_LIMIT,
    ResetWalletLimit,
    resetWalletLimit,
    UPDATE_WALLET_LIMIT,
    UPDATE_WALLET_SWITCH_OFF_LIMIT_EDIT_POPUP_VISIBILITY,
    updatePaymentData,
    updateWalletLimit,
    updateWalletSwitchOffLimitPopupResettingState,
    updateWalletSwitchOffLimitPopupSavingState,
    updateWalletSwitchOffLimitPopupValidation,
    updateWalletSwitchOffLimitPopupValue,
    UpdateWalletSwitchOffLimitPopupVisibility,
    WalletSwitchOffLimitPopupAction
} from '../../actions/walletSwitchOffLimitEditPopup';
import {
    StyledLink,
    StyledLinkTheme
} from '../../components/StyledLink/StyledLink';
import { WalletSwitchOffLimitEditPopupWrapperEpicDeps } from '../../components/WalletSwitchOffLimitEditPopupWrapper/WalletSwitchOffLimitEditPopupWrapper';
import {
    getValidationResultFromApiError,
    mapSwitchOffLimitPaymentDataFromApiFormat
} from '../../mappers/wallet/wallet';
import {
    formatNumber,
    parseNumber
} from '../../service/FormatHelpers/FormatHelpers';
import { UrlHelpers } from '../../service/UrlHelpers';
import { validateSwitchOffLimitPopupValue } from '../../service/WalletSwitchOffLimitPopupHelpers/WalletSwitchOffLimitPopupHelpers';
import { LinkTarget } from '../../typings/common';
import { ResetAutoOverdraftLimit } from '../../typings/graphql-grid';
import { WalletSwitchOffLimitPopupState } from '../../typings/walletState';

import * as keyset from './walletSwitchOffLimitEditPopup.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

const fetchPaymentDataEpic: Epic<
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupState,
    WalletSwitchOffLimitEditPopupWrapperEpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(FETCH_PAYMENT_DATA).mergeMap(() => {
        const state = store$.value;

        return Observable.fromPromise(
            deps.walletDataProvider.fetchSwitchOffLimitPaymentData(state.login)
        )
            .mergeMap(
                (data): Observable<WalletSwitchOffLimitPopupAction> => {
                    if (data.client.autoOverdraftPaymentOptions.length === 0) {
                        return Observable.of(
                            updateWalletSwitchOffLimitPopupValidation({
                                valid: false,
                                errors: [
                                    {
                                        type: 'data-fetch',
                                        text: i18nRaw('no-payers', {
                                            addPayerLink: (
                                                <StyledLink
                                                    theme={
                                                        StyledLinkTheme.alert
                                                    }
                                                    target={LinkTarget.blank}
                                                    href={
                                                        'https://balance.yandex.ru/persons.xml'
                                                    }
                                                >
                                                    {i18n('add-payer')}
                                                </StyledLink>
                                            )
                                        }),
                                        path: 'general'
                                    }
                                ]
                            })
                        );
                    }

                    const availableAutoOverdraftPaymentOptions = data.client.autoOverdraftPaymentOptions.filter(
                        option =>
                            option.paymentMethods &&
                            option.paymentMethods.length
                    );

                    if (availableAutoOverdraftPaymentOptions.length === 0) {
                        return Observable.of(
                            updateWalletSwitchOffLimitPopupValidation({
                                valid: false,
                                errors: [
                                    {
                                        type: 'data-fetch',
                                        text: i18nRaw('no-payment-methods', {
                                            writeToUsLink: (
                                                <StyledLink
                                                    theme={
                                                        StyledLinkTheme.alert
                                                    }
                                                    target={LinkTarget.blank}
                                                    href={UrlHelpers.getCommonHelpUrl(
                                                        'direct/payments/faq.html#other',
                                                        state.tld
                                                    )}
                                                >
                                                    {i18n('write-to-us')}
                                                </StyledLink>
                                            )
                                        }),
                                        path: 'general'
                                    }
                                ]
                            })
                        );
                    }

                    const autoOverdraftPaymentOptions = mapSwitchOffLimitPaymentDataFromApiFormat(
                        availableAutoOverdraftPaymentOptions
                    );

                    if (!autoOverdraftPaymentOptions.selectedPayerId) {
                        autoOverdraftPaymentOptions.selectedPayerId =
                            autoOverdraftPaymentOptions.payerData[0].id;
                        autoOverdraftPaymentOptions.selectedPaymentMethodId =
                            autoOverdraftPaymentOptions.paymentMethods[
                                autoOverdraftPaymentOptions.selectedPayerId
                            ][0].id;
                    }

                    return Observable.merge(
                        Observable.of(
                            updatePaymentData({
                                payerData:
                                    autoOverdraftPaymentOptions.payerData,
                                paymentMethods:
                                    autoOverdraftPaymentOptions.paymentMethods
                            })
                        ),
                        Observable.of(
                            updateWalletSwitchOffLimitPopupValue({
                                paymentMethodId:
                                    autoOverdraftPaymentOptions.selectedPaymentMethodId,
                                payerId:
                                    autoOverdraftPaymentOptions.selectedPayerId
                            })
                        )
                    );
                }
            )
            .catch(() => {
                return Observable.of(
                    updateWalletSwitchOffLimitPopupValidation({
                        valid: false,
                        errors: [
                            {
                                type: 'data-fetch',
                                text: i18n('try-later'),
                                path: 'general'
                            }
                        ]
                    })
                );
            });
    });
};

const closeWalletSwitchOffLimitEditPopupEpic: Epic<
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupState,
    WalletSwitchOffLimitEditPopupWrapperEpicDeps
> = (action$, store$, deps) => {
    action$
        .ofType(UPDATE_WALLET_SWITCH_OFF_LIMIT_EDIT_POPUP_VISIBILITY)
        .filter(
            (action: UpdateWalletSwitchOffLimitPopupVisibility) =>
                action.isVisible === false
        )
        .subscribe(() => {
            if (deps.actionCallbacks.onClose) {
                deps.actionCallbacks.onClose();
            }
        });

    return Observable.of();
};

const updateWalletLimitEpic: Epic<
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupState,
    WalletSwitchOffLimitEditPopupWrapperEpicDeps
> = (action$, store$, deps) => {
    action$.ofType(UPDATE_WALLET_LIMIT).subscribe(() => {
        if (deps.actionCallbacks.onSet) {
            const value = store$.value.value;
            const parsedSum =
                value.sum !== undefined ? parseNumber(value.sum) : 0;

            deps.actionCallbacks.onSet({
                sum: parsedSum,
                payerId: value.payerId || '',
                paymentMethodId: value.paymentMethodId || ''
            });
        }
    });

    return Observable.of();
};

const resetWalletLimitEpic: Epic<
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupState,
    WalletSwitchOffLimitEditPopupWrapperEpicDeps
> = (action$, store$, deps) => {
    action$.ofType(RESET_WALLET_LIMIT).subscribe((action: ResetWalletLimit) => {
        if (deps.actionCallbacks.onReset) {
            deps.actionCallbacks.onReset(parseNumber(action.sum));
        }
    });

    return Observable.of();
};

const makeUpdateWalletLimitRequestEpic: Epic<
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupState,
    WalletSwitchOffLimitEditPopupWrapperEpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(MAKE_UPDATE_WALLET_LIMIT_REQUEST).mergeMap(() => {
        const state = store$.value;

        const clientValidationResult = validateSwitchOffLimitPopupValue(
            state.value,
            {
                min: state.minSum,
                max: state.maxSum
            },
            state.currency
        );

        if (clientValidationResult.valid === false) {
            return Observable.of(
                updateWalletSwitchOffLimitPopupValidation(
                    clientValidationResult
                )
            );
        } else {
            const setProgressStatus$ = Observable.of(
                updateWalletSwitchOffLimitPopupSavingState(true)
            );
            const { paymentMethodId, payerId, sum } = state.value;
            const parsedSum =
                sum === undefined || sum === '' ? null : parseNumber(sum);

            let paymentMethodName: string;

            if (state.paymentData && payerId) {
                const paymentMethods =
                    state.paymentData.paymentMethods[payerId] || [];

                paymentMethodName = paymentMethods.reduce(
                    (acc, el) => (el.id === paymentMethodId ? el.name : acc),
                    ''
                );
            }

            const makeRequest$ = Observable.fromPromise(
                deps.walletMutationExecutor.setAutoOverdraftLimit(
                    paymentMethodId || '',
                    Number(payerId),
                    parsedSum
                )
            )
                .map(updateWalletLimit)
                .catch(err => {
                    return Observable.of(
                        updateWalletSwitchOffLimitPopupValidation(
                            getValidationResultFromApiError(
                                err,
                                state.currency,
                                paymentMethodName
                            )
                        )
                    ).merge(
                        Observable.of(
                            updateWalletSwitchOffLimitPopupSavingState(false)
                        )
                    );
                });

            return Observable.concat(setProgressStatus$, makeRequest$);
        }
    });
};

const makeResetWalletLimitRequestEpic: Epic<
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupAction,
    WalletSwitchOffLimitPopupState,
    WalletSwitchOffLimitEditPopupWrapperEpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(MAKE_RESET_WALLET_LIMIT_REQUEST).mergeMap(() => {
        const state = store$.value;
        const setProgressStatus$ = Observable.of(
            updateWalletSwitchOffLimitPopupResettingState(true)
        );

        const makeRequest$ = Observable.fromPromise(
            deps.walletMutationExecutor.resetAutoOverdraftLimit()
        )
            .map((data: ResetAutoOverdraftLimit) =>
                resetWalletLimit(
                    formatNumber(
                        Number(data.resetAutoOverdraftLimit.autoOverdraftLimit),
                        2
                    )
                )
            )
            .catch(err => {
                return Observable.of(
                    updateWalletSwitchOffLimitPopupValidation(
                        getValidationResultFromApiError(err, state.currency)
                    )
                ).merge(
                    Observable.of(
                        updateWalletSwitchOffLimitPopupResettingState(false)
                    )
                );
            });

        return Observable.concat(setProgressStatus$, makeRequest$);
    });
};

export const walletSwitchOffLimitEditPopupWrapperEpics = combineEpics(
    fetchPaymentDataEpic,
    makeUpdateWalletLimitRequestEpic,
    updateWalletLimitEpic,
    resetWalletLimitEpic,
    makeResetWalletLimitRequestEpic,
    closeWalletSwitchOffLimitEditPopupEpic
);
